import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Trash2, Edit3, Eye, FileText, X, Paperclip,
  Image, File, UserCircle, SplitSquareHorizontal, ArrowUpDown,
  ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import { MessageToolbar } from "../components/MessageToolbar";

const API = '/api';

const AttachmentBadge = ({ att, onRemove }) => {
  const iconMap = { image: Image, pdf: File, contact: UserCircle };
  const Icon = iconMap[att.type] || File;
  return (
    <div className="flex items-center gap-2 bg-zinc-950/50 border border-zinc-800 rounded-md px-2.5 py-1.5">
      <Icon className="w-3.5 h-3.5 text-zinc-400" />
      <span className="text-xs text-zinc-300 truncate max-w-[120px]">{att.filename}</span>
      {onRemove && (
        <button onClick={() => onRemove(att.id)} className="text-zinc-600 hover:text-red-400 transition-colors duration-200">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: "", content: "", attachment_ids: [],
    split_messages: false, attachment_order: "after"
  });
  const [previews, setPreviews] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "", phone: "", email: "", company: "", title: ""
  });
  const textareaRef = useRef(null);
  const partTextareaRefs = useRef([]);
  const splitToolbarRef = useRef(null);
  const [activeSplitPart, setActiveSplitPart] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/attachments`)
      ]);
      setTemplates(t.data);
      setAttachments(a.data);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveTemplate = async () => {
    if (!form.name || !form.content) return toast.error("Completa todos los campos");
    try {
      if (editId) {
        await axios.put(`${API}/templates/${editId}`, form);
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/templates`, form);
        toast.success("Plantilla creada");
      }
      setShowCreate(false);
      setEditId(null);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error("Error al guardar plantilla");
    }
  };

  const resetForm = () => {
    setForm({ name: "", content: "", attachment_ids: [], split_messages: false, attachment_order: "after" });
    setActiveSplitPart(0);
    setShowPreview(false);
    setPreviews([]);
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success("Plantilla eliminada");
      fetchData();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const previewSpintax = async () => {
    try {
      const res = await axios.post(`${API}/templates/preview-spintax`, { content: form.content });
      setPreviews(res.data.previews);
      setShowPreview(true);
    } catch (e) {
      toast.error("Error al generar previsualizacion");
    }
  };

  const startEdit = (t) => {
    setForm({
      name: t.name, content: t.content,
      attachment_ids: t.attachment_ids || [],
      split_messages: t.split_messages || false,
      attachment_order: t.attachment_order || "after"
    });
    setActiveSplitPart(0);
    setEditId(t.id);
    setShowCreate(true);
    setShowPreview(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setAttachments(prev => [res.data, ...prev]);
      setForm(prev => ({ ...prev, attachment_ids: [...prev.attachment_ids, res.data.id] }));
      toast.success("Archivo subido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const createContactCard = async () => {
    if (!contactForm.name || !contactForm.phone) return toast.error("Nombre y telefono son obligatorios");
    try {
      const res = await axios.post(`${API}/contact-cards`, contactForm);
      setAttachments(prev => [res.data, ...prev]);
      setForm(prev => ({ ...prev, attachment_ids: [...prev.attachment_ids, res.data.id] }));
      setShowContactForm(false);
      setContactForm({ name: "", phone: "", email: "", company: "", title: "" });
      toast.success("Tarjeta de contacto creada");
    } catch (e) {
      toast.error("Error al crear tarjeta");
    }
  };

  const toggleAttachment = (attId) => {
    setForm(prev => ({
      ...prev,
      attachment_ids: prev.attachment_ids.includes(attId)
        ? prev.attachment_ids.filter(id => id !== attId)
        : [...prev.attachment_ids, attId]
    }));
  };

  const deleteAttachment = async (id) => {
    try {
      await axios.delete(`${API}/attachments/${id}`);
      setAttachments(prev => prev.filter(a => a.id !== id));
      setForm(prev => ({ ...prev, attachment_ids: prev.attachment_ids.filter(a => a !== id) }));
      toast.success("Archivo eliminado");
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const selectedAttachments = attachments.filter(a => form.attachment_ids.includes(a.id));

  // --- Split message helpers ---
  const splitContent = (content) => {
    if (!content) return [""];
    const parts = content.split("---").map(p => p.trim());
    return parts.length > 0 ? parts : [""];
  };

  const updateMessagePart = (index, value) => {
    const parts = splitContent(form.content);
    parts[index] = value;
    setForm(prev => ({ ...prev, content: parts.join("\n---\n") }));
  };

  const addMessagePart = () => {
    const parts = splitContent(form.content);
    parts.push("");
    setForm(prev => ({ ...prev, content: parts.join("\n---\n") }));
    setActiveSplitPart(parts.length - 1);
  };

  const removeMessagePart = (index) => {
    const parts = splitContent(form.content).filter((_, i) => i !== index);
    if (parts.length === 0) parts.push("");
    setForm(prev => ({ ...prev, content: parts.join("\n---\n") }));
    if (activeSplitPart >= parts.length) setActiveSplitPart(Math.max(0, parts.length - 1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="templates-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">Plantillas</h1>
          <p className="text-sm text-zinc-500 mt-1">Crea mensajes con Spintax, adjuntos y mensajes divididos</p>
        </div>
        <button
          data-testid="create-template-btn"
          onClick={() => { setShowCreate(true); setEditId(null); resetForm(); }}
          className="bg-blue-600 hover:bg-blue-700 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)] rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95"
        >
          <Plus className="w-4 h-4" /> Nueva Plantilla
        </button>
      </div>

      {showCreate && (
        <div data-testid="template-editor" className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-white uppercase">
              {editId ? "Editar Plantilla" : "Nueva Plantilla"}
            </h3>
            <button onClick={() => { setShowCreate(false); setEditId(null); }} className="text-zinc-500 hover:text-white transition-colors duration-200">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Nombre</label>
            <input
              data-testid="template-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200"
              placeholder="Ej: Promo Enero"
            />
          </div>

          {/* Split Messages Toggle */}
          <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <SplitSquareHorizontal className="w-4 h-4 text-cyan-400" />
              <div>
                <p className="text-sm text-white font-medium">Enviar en mensajes separados</p>
                <p className="text-[10px] text-zinc-500">
                  Cada bloque se enviará como un mensaje individual
                </p>
              </div>
            </div>
            <button
              data-testid="toggle-split-messages"
              onClick={() => setForm({ ...form, split_messages: !form.split_messages })}
              className={`w-11 h-6 rounded-full transition-colors duration-200 relative flex-shrink-0 ${
                form.split_messages ? 'bg-blue-600' : 'bg-zinc-700'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${
                form.split_messages ? 'translate-x-5' : 'translate-x-0.5'
              }`} />
            </button>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
              {form.split_messages ? 'Mensajes Separados (Spintax)' : 'Contenido del Mensaje (Spintax)'}
            </label>

            {form.split_messages ? (
              <div className="space-y-3">
                <MessageToolbar
                  textareaRef={splitToolbarRef}
                  value={splitContent(form.content)[activeSplitPart] || ""}
                  onChange={(val) => updateMessagePart(activeSplitPart, val)}
                  hideDivider
                />
                {splitContent(form.content).map((part, i) => (
                  <div key={`msg-part-${i}`} className="relative group">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-mono font-bold text-cyan-500 bg-cyan-500/10 px-2 py-0.5 rounded">
                        MSG {i + 1}
                      </span>
                      {splitContent(form.content).length > 1 && (
                        <button
                          data-testid={`remove-msg-part-${i}`}
                          onClick={() => removeMessagePart(i)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-all duration-200"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <textarea
                      ref={el => { partTextareaRefs.current[i] = el; }}
                      data-testid={`split-part-textarea-${i}`}
                      value={part}
                      onFocus={() => {
                        setActiveSplitPart(i);
                        splitToolbarRef.current = partTextareaRefs.current[i];
                      }}
                      onChange={(e) => updateMessagePart(i, e.target.value)}
                      rows={3}
                      className={`w-full bg-zinc-950/50 border rounded-md px-3 py-3 text-sm font-mono text-cyan-400/90 placeholder:text-zinc-600 outline-none transition-colors duration-200 leading-relaxed ${
                        activeSplitPart === i ? 'border-cyan-500/50 ring-1 ring-cyan-500/20' : 'border-zinc-800'
                      }`}
                      placeholder={`Escribe el mensaje ${i + 1}...`}
                    />
                  </div>
                ))}
                <button
                  data-testid="add-msg-part-btn"
                  onClick={addMessagePart}
                  className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors duration-200 bg-cyan-500/5 hover:bg-cyan-500/10 border border-dashed border-cyan-500/20 rounded-md px-3 py-2.5 w-full justify-center"
                >
                  <Plus className="w-3.5 h-3.5" /> Agregar Mensaje
                </button>
              </div>
            ) : (
              <>
                <MessageToolbar
                  textareaRef={textareaRef}
                  value={form.content}
                  onChange={(val) => setForm({ ...form, content: val })}
                />
                <textarea
                  ref={textareaRef}
                  data-testid="template-content-input"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={6}
                  className="w-full bg-zinc-950/50 border border-zinc-800 border-t-0 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 rounded-b-md px-3 py-3 text-sm font-mono text-cyan-400/90 placeholder:text-zinc-600 outline-none transition-colors duration-200 leading-relaxed"
                  placeholder={"{Hola|Buen dia} {{nombre}}, te comparto nuestra promo especial. Responde SI para mas info"}
                />
              </>
            )}
            <p className="text-[10px] text-zinc-600 mt-1.5 font-mono">
              Click derecho en variable para agregar limite de caracteres. Ej: {"{{nombreL20}}"} = max 20 chars
            </p>
          </div>

          {/* Adjuntos desactivados temporalmente */}

          {/* Contact Card Creator */}
          {showContactForm && (
            <div data-testid="contact-card-form" className="bg-zinc-950 border border-white/10 rounded-lg p-4 space-y-3 animate-fade-in">
              <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                <UserCircle className="w-4 h-4 text-cyan-400" /> Crear Tarjeta de Contacto
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "name", label: "Nombre *", placeholder: "Juan Perez" },
                  { key: "phone", label: "Telefono *", placeholder: "+521234567890" },
                  { key: "email", label: "Email", placeholder: "juan@empresa.com" },
                  { key: "company", label: "Empresa", placeholder: "Mi Empresa SA" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">{f.label}</label>
                    <input
                      data-testid={`contact-card-${f.key}`}
                      value={contactForm[f.key]}
                      onChange={(e) => setContactForm({ ...contactForm, [f.key]: e.target.value })}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-1.5 text-xs text-white outline-none transition-colors duration-200"
                      placeholder={f.placeholder}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="save-contact-card-btn"
                  onClick={createContactCard}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-200"
                >
                  Crear Tarjeta
                </button>
                <button
                  onClick={() => setShowContactForm(false)}
                  className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md px-3 py-1.5 text-xs transition-colors duration-200"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              data-testid="save-template-btn"
              onClick={saveTemplate}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 active:scale-95"
            >
              {editId ? "Actualizar" : "Guardar"}
            </button>
            <button
              data-testid="preview-spintax-btn"
              onClick={previewSpintax}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200"
            >
              <Eye className="w-4 h-4" /> Previsualizar
            </button>
          </div>

          {showPreview && previews.length > 0 && (
            <div data-testid="spintax-preview" className="bg-zinc-950 border border-white/5 rounded-lg p-4 space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Variaciones generadas:</p>
              {previews.map((p, i) => (
                <div key={`part-${i}-${p?.slice(0,10)}`} className="bg-zinc-900/50 rounded-md p-3 text-sm text-zinc-300 border-l-2 border-blue-500/30">{p}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((t, i) => (
          <div
            key={t.id}
            data-testid={`template-card-${i}`}
            className="bg-zinc-900 border border-white/5 rounded-lg p-5 space-y-3 hover:border-white/10 transition-colors duration-200"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">{t.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-[10px] font-mono text-zinc-600">
                    {new Date(t.created_at).toLocaleDateString('es-ES')}
                  </p>
                  {t.split_messages && (
                    <span className="text-[9px] font-mono text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">MULTI-MSG</span>
                  )}
                  {(t.attachment_ids || []).length > 0 && (
                    <span className="text-[9px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                      {t.attachment_ids.length} ADJUNTO{t.attachment_ids.length > 1 ? 'S' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={`edit-template-${i}`}
                  onClick={() => startEdit(t)}
                  className="p-1.5 hover:bg-zinc-800 rounded transition-colors duration-200 text-zinc-400 hover:text-white"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  data-testid={`delete-template-${i}`}
                  onClick={() => deleteTemplate(t.id)}
                  className="p-1.5 hover:bg-red-500/10 rounded transition-colors duration-200 text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="bg-zinc-950/50 rounded-md p-3">
              <p className="text-xs font-mono text-cyan-500/70 leading-relaxed line-clamp-3">{t.content}</p>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full text-center py-12">
            <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay plantillas creadas</p>
          </div>
        )}
      </div>
    </div>
  );
}
